// Palette recents — localStorage layer + server sync.
// Stores up to 20 recent entries keyed per-org/user; exposes 8 for display.
// Storage dedupe key: kind+id (latest url wins on revisit).
// Display dedupe: by url, preferring 'record' kind over 'managed-contract'.
// Entries with kind 'nav' are rejected — contract, record, and invoice kinds persist.
//
// Server sync (GET/PUT /profile/nav-recents):
// - Read: localStorage is the placeholder; the fetched server list is merged
//   with localStorage (per kind+id, latest visitedAt wins) and written back.
// - Write: record()/clear() stay optimistic-local; a shared 2 s trailing-
//   debounced full-replace PUT re-reads localStorage at send time (localStorage
//   is shared across tabs, which closes most of the cross-tab race), mapping
//   entries through a strict wire-field whitelist — the backend's strict
//   per-kind schemas reject extras. clear() PUTs an empty list immediately.
// - A window 'storage' listener converges sibling tabs live for this key.
// - Failures are swallowed; recents must never throw into React. Any 4xx
//   (endpoint not deployed, permissions drift) flips silent local-only mode
//   for the rest of the session.

import { useCallback, useEffect, useMemo } from "react";
import { useQuery, useQueryClient, skipToken } from "@tanstack/react-query";
import { useAuth } from "../contexts/AuthContext";
import { profileApi, ApiError } from "../services/api";
import type { RecentEntry, ManagedContractEntry } from "../types/navRecents";

// ─── Types ────────────────────────────────────────────────────────────────────
// The persisted entry union lives in types/navRecents (shared with the api
// client wire methods); re-exported here so palette consumers keep importing
// from the hook.

export type {
  RecentEntry,
  ManagedContractEntry,
  DraftContractEntry,
  RecordEntry,
  InvoiceEntry,
} from "../types/navRecents";

// ─── Constants ────────────────────────────────────────────────────────────────

const STORAGE_VERSION = "v1";
const MAX_STORED = 20;
const MAX_SHOWN = 8;
const PUT_DEBOUNCE_MS = 2000;

const NAV_RECENTS_QUERY_KEY = ["profile", "nav-recents"] as const;

function storageKey(organisationId: string, userId: string): string {
  return `cg.palette.recents.${STORAGE_VERSION}.${organisationId}.${userId}`;
}

// ─── Module sync state ────────────────────────────────────────────────────────
// Shared by every hook instance: one debounce timer so write bursts across
// instances (a palette selection + the route-observer visit it triggers)
// coalesce into a single PUT, and a session-wide local-only flag. localOnly
// deliberately persists across user switches within a tab session — a 4xx
// means the deployed backend lacks the endpoint (or rejects this client),
// which re-login won't change; a page reload re-probes.

const syncState = {
  localOnly: false,
  putTimer: null as ReturnType<typeof setTimeout> | null,
};

/** Reset module-level sync state — a seam for test isolation. */
export function resetNavRecentsSync(): void {
  syncState.localOnly = false;
  if (syncState.putTimer) clearTimeout(syncState.putTimer);
  syncState.putTimer = null;
}

// ─── Storage helpers ──────────────────────────────────────────────────────────

function dedupeKey(entry: RecentEntry): string {
  return `${entry.kind}::${entry.id}`;
}

/** Priority score for display dedupe: higher wins when multiple entries share a url. */
function kindPriority(entry: RecentEntry): number {
  if (entry.kind === "record") return 2;
  if (entry.kind === "managed-contract") return 1;
  return 0;
}

function loadFromStorage(key: string): RecentEntry[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    return JSON.parse(raw) as RecentEntry[];
  } catch {
    return [];
  }
}

function saveToStorage(key: string, entries: RecentEntry[]): void {
  localStorage.setItem(key, JSON.stringify(entries));
}

function newestFirst(a: RecentEntry, b: RecentEntry): number {
  return new Date(b.visitedAt).getTime() - new Date(a.visitedAt).getTime();
}

/** Keep the newest MAX_STORED entries by visitedAt. */
function capEntries(entries: RecentEntry[]): RecentEntry[] {
  return [...entries].sort(newestFirst).slice(0, MAX_STORED);
}

/** Union two lists by kind+id, latest visitedAt winning, capped at MAX_STORED. */
function mergeEntries(a: RecentEntry[], b: RecentEntry[]): RecentEntry[] {
  const byKey = new Map<string, RecentEntry>();
  for (const entry of [...a, ...b]) {
    const dk = dedupeKey(entry);
    const existing = byKey.get(dk);
    if (
      !existing ||
      new Date(entry.visitedAt).getTime() > new Date(existing.visitedAt).getTime()
    ) {
      byKey.set(dk, entry);
    }
  }
  return capEntries([...byKey.values()]);
}

/** Compute the display list: newest-first, deduplicated by url (record kind preferred), capped at MAX_SHOWN. */
function computeShown(entries: RecentEntry[]): RecentEntry[] {
  const sorted = [...entries].sort(newestFirst);

  const seenUrls = new Map<string, RecentEntry>();
  for (const entry of sorted) {
    const existing = seenUrls.get(entry.url);
    if (!existing || kindPriority(entry) > kindPriority(existing)) {
      seenUrls.set(entry.url, entry);
    }
  }

  // Preserve newest-first order after deduplication
  const deduped = sorted.filter((e) => seenUrls.get(e.url) === e);
  return deduped.slice(0, MAX_SHOWN);
}

// ─── Server sync helpers ──────────────────────────────────────────────────────

function isClientError(err: unknown): boolean {
  return err instanceof ApiError && err.status >= 400 && err.status < 500;
}

// The backend validates entries with strict per-kind schemas — send ONLY the
// persisted fields, dropping anything palette internals may have attached to
// the stored entry.
function toWireEntry(entry: RecentEntry): RecentEntry {
  const base = {
    id: entry.id,
    url: entry.url,
    title: entry.title,
    visitedAt: entry.visitedAt,
    ...(entry.ctx !== undefined ? { ctx: entry.ctx } : {}),
  };
  if (entry.kind === "record") {
    return {
      ...base,
      kind: "record",
      recordType: entry.recordType,
      contractId: entry.contractId,
    };
  }
  if (entry.kind === "invoice") {
    return { ...base, kind: "invoice", contractId: entry.contractId };
  }
  return { ...base, kind: entry.kind };
}

// Full-replace PUT of the current localStorage list. Reading localStorage at
// send time (not at schedule time) folds in sibling-tab writes that landed
// during the debounce window. Failures are swallowed — recents must never
// surface errors; a client error flips silent local-only mode.
async function sendRecents(key: string): Promise<void> {
  try {
    await profileApi.updateNavRecents(loadFromStorage(key).map(toWireEntry));
  } catch (err) {
    if (isClientError(err)) syncState.localOnly = true;
  }
}

function schedulePut(key: string): void {
  if (syncState.localOnly) return;
  if (syncState.putTimer) clearTimeout(syncState.putTimer);
  syncState.putTimer = setTimeout(() => {
    syncState.putTimer = null;
    void sendRecents(key);
  }, PUT_DEBOUNCE_MS);
}

function cancelScheduledPut(): void {
  if (syncState.putTimer) clearTimeout(syncState.putTimer);
  syncState.putTimer = null;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export interface RecordVisitArgs {
  id: string;
  title: string;
  ctx?: string;
  pathname: string;
  search: string;
}

export interface UseNavRecentsResult {
  shown: RecentEntry[];
  record: (entry: RecentEntry) => void;
  recordVisit: (args: RecordVisitArgs) => void;
  clear: () => void;
}

/**
 * Manages the palette recent-items list: localStorage as the optimistic
 * canonical client state, server-synced via GET/PUT /profile/nav-recents.
 * Entries are namespaced per-organisation and per-user; no query runs
 * without both. Nav entries (kind: 'nav') are silently ignored.
 */
export function useNavRecents(): UseNavRecentsResult {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const key =
    user?.organisationId && user?.userId
      ? storageKey(user.organisationId, user.userId)
      : null;

  // Local read used as the optimistic placeholder while the GET is in flight
  // and as the fallback when the query is in error state (network/5xx after
  // default retries) — the local layer must keep working either way.
  const readLocal = useCallback(
    (): RecentEntry[] => (key ? loadFromStorage(key) : []),
    [key],
  );

  const { data } = useQuery({
    queryKey: NAV_RECENTS_QUERY_KEY,
    // skipToken (rather than an enabled flag alone) keeps the fetcher fully
    // typed on a non-null key. localOnly is read at render time: the render
    // triggered by the flag-setting fetch settles the query for the session.
    queryFn:
      key === null || syncState.localOnly
        ? skipToken
        : async (): Promise<RecentEntry[]> => {
            try {
              const res = await profileApi.getNavRecents();
              const merged = mergeEntries(res.entries, loadFromStorage(key));
              saveToStorage(key, merged);
              return merged;
            } catch (err) {
              // 4xx (endpoint not deployed / rejected) → silent local-only
              // for the session: serve the local list as a SUCCESS so the
              // palette never sees an error, and stop querying. No retry on
              // 4xx exists by construction — a client error never escapes
              // the queryFn, so retry logic never sees one.
              if (isClientError(err)) {
                syncState.localOnly = true;
                return loadFromStorage(key);
              }
              throw err; // network/5xx → default query retry behaviour
            }
          },
    placeholderData: readLocal,
    staleTime: 5 * 60 * 1000,
  });

  const shown = useMemo(
    () => computeShown(data ?? readLocal()),
    [data, readLocal],
  );

  // Converge sibling tabs live: their record()/clear() writes localStorage,
  // which fires 'storage' here. Merge (don't replace) so an in-flight local
  // write in this tab is never lost.
  useEffect(() => {
    if (!key) return;
    const recentsKey = key;
    function onStorage(e: StorageEvent) {
      if (e.key !== recentsKey) return;
      const merged = mergeEntries(
        queryClient.getQueryData<RecentEntry[]>(NAV_RECENTS_QUERY_KEY) ?? [],
        loadFromStorage(recentsKey),
      );
      queryClient.setQueryData<RecentEntry[]>(NAV_RECENTS_QUERY_KEY, merged);
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [key, queryClient]);

  const record = useCallback(
    (entry: RecentEntry) => {
      // nav entries are never persisted
      if ((entry as { kind: string }).kind === "nav") return;

      if (!key) return;

      const existing = loadFromStorage(key);

      // Storage dedupe: replace any entry with the same kind+id (latest url
      // wins), keep the newest MAX_STORED.
      const dk = dedupeKey(entry);
      const next = capEntries([
        ...existing.filter((e) => dedupeKey(e) !== dk),
        entry,
      ]);

      saveToStorage(key, next);
      // Optimistic: the cache update re-renders every mounted instance now;
      // the server write trails behind the shared debounce.
      queryClient.setQueryData<RecentEntry[]>(NAV_RECENTS_QUERY_KEY, next);
      schedulePut(key);
    },
    [key, queryClient],
  );

  const recordVisit = useCallback(
    ({ id, title, ctx, pathname, search }: RecordVisitArgs) => {
      const url = search ? `${pathname}${search}` : pathname;
      const entry: ManagedContractEntry = {
        kind: "managed-contract",
        id,
        title,
        ...(ctx !== undefined ? { ctx } : {}),
        url,
        visitedAt: new Date().toISOString(),
      };
      record(entry);
    },
    [record],
  );

  const clear = useCallback(() => {
    // A pending debounced PUT would just re-send the now-empty list — cancel
    // it so the immediate clear PUT below is the only request.
    cancelScheduledPut();
    queryClient.setQueryData<RecentEntry[]>(NAV_RECENTS_QUERY_KEY, []);
    if (!key) return;
    localStorage.removeItem(key);
    // Immediate (not debounced) server clear: localStorage is already empty,
    // so the regular send path PUTs an empty entries array.
    if (!syncState.localOnly) void sendRecents(key);
  }, [key, queryClient]);

  return { shown, record, recordVisit, clear };
}
